<template>
  <div class="chart-data-table">
    <div class="table-container">
      <div class="shadow-indicator shadow-top" ref="topShadow"></div>

      <div class="app-table-responsive" ref="tableScroll" @scroll="handleScroll">
        <table>
          <tbody>
            <tr
              v-for="(item, index) in data"
              :key="index"
              class="data-row"
              @mouseenter="$emit('highlight', index)"
              @mouseleave="$emit('reset-highlight')"
            >
              <td class="color-cell">
                <span
                  class="color-indicator"
                  :style="{
                    backgroundColor: colors[index % colors.length]
                  }"
                ></span>
              </td>
              <td class="name-cell">
                <span class="item-name" :title="item.name">{{ item.name }}</span>
              </td>
              <td class="text-right">{{ formatNumber(item.value) }}</td>
              <td class="text-right">
                <div class="percent-container">
                  <div
                    class="percent-bar"
                    :style="{
                      width: `${((item.value / totalValue) * 100).toFixed(1)}%`,
                      backgroundColor: colors[index % colors.length]
                    }"
                  ></div>
                  <span class="percent-text"
                    >{{ ((item.value / totalValue) * 100).toFixed(1) }}%</span
                  >
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot v-if="data.length > 1" ref="tableFoot">
            <tr class="total-row">
              <td colspan="2">
                <strong>{{ $t('Total') }}</strong>
              </td>
              <td class="text-right">
                <strong>{{ formatNumber(totalValue) }}</strong>
              </td>
              <td class="text-right"><strong>100%</strong></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div class="shadow-indicator shadow-bottom" ref="bottomShadow"></div>
    </div>
  </div>
</template>

<script>
import { helper } from '@core/mixins/helper.js';

export default {
  name: 'ChartDataTable',
  props: {
    data: {
      type: Array,
      required: true
    },
    colors: {
      type: Array,
      required: true
    }
  },
  mixins: [helper],
  data() {
    return {
      showTopShadow: false,
      showBottomShadow: true,
      footerHeight: 0
    };
  },
  computed: {
    totalValue() {
      return this.data.reduce((sum, item) => sum + item.value, 0);
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.calculateFooterHeight();
      this.checkScrollShadows();

      // Pencere boyutu değiştiğinde gölgeleri güncelle
      window.addEventListener('resize', this.handleResize);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    handleScroll() {
      this.checkScrollShadows();
    },
    handleResize() {
      this.calculateFooterHeight();
      this.checkScrollShadows();
    },
    calculateFooterHeight() {
      if (this.$refs.tableFoot) {
        this.footerHeight = this.$refs.tableFoot.offsetHeight;
      } else {
        this.footerHeight = 0;
      }
    },
    checkScrollShadows() {
      const scrollElement = this.$refs.tableScroll;
      if (!scrollElement) return;

      const isScrollable = scrollElement.scrollHeight > scrollElement.clientHeight;

      this.showTopShadow = scrollElement.scrollTop > 0;

      const scrollBottom = scrollElement.scrollTop + scrollElement.clientHeight;
      const scrollableHeight = scrollElement.scrollHeight - this.footerHeight;

      this.showBottomShadow = isScrollable && scrollBottom < scrollableHeight - 2;

      if (this.$refs.topShadow) {
        this.$refs.topShadow.style.opacity = this.showTopShadow ? '1' : '0';
      }

      if (this.$refs.bottomShadow) {
        this.$refs.bottomShadow.style.opacity = this.showBottomShadow ? '1' : '0';

        if (this.footerHeight > 0) {
          this.$refs.bottomShadow.style.bottom = `${this.footerHeight}px`;
        } else {
          this.$refs.bottomShadow.style.bottom = '0';
        }
      }
    }
  },
  watch: {
    data() {
      this.$nextTick(() => {
        this.calculateFooterHeight();
        this.checkScrollShadows();
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.chart-data-table {
  width: 100%;
}

.table-container {
  position: relative;
  overflow: hidden;
}

.app-table-responsive {
  overflow-x: auto;
  max-height: 300px;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scroll-behavior: smooth;
  position: relative;
}

.shadow-indicator {
  position: absolute;
  left: 0;
  right: 0;
  height: 12px;
  pointer-events: none;
  z-index: 2;
  transition: opacity 0.3s ease;
}

.shadow-top {
  top: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 100%);
  opacity: 0;

  body.dark-layout & {
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0.1) 0%,
      rgba(255, 255, 255, 0) 100%
    );
  }
}

.shadow-bottom {
  bottom: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0) 100%);
  opacity: 1;

  body.dark-layout & {
    background: linear-gradient(to top, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 100%);
  }
}

table {
  width: 100%;
  border-collapse: collapse;
  font-size: 1rem;
}

td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #eee;

  body.dark-layout & {
    border-bottom: 1px solid #3b4253;
  }
}

.text-right {
  text-align: right;
}

.data-row {
  scroll-snap-align: start;

  &:hover {
    background-color: #f8f9fa;
    cursor: pointer;

    body.dark-layout & {
      background-color: #161d31;
    }
  }
}

.name-cell {
  max-width: 200px;
}

.item-name {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.percent-container {
  position: relative;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.percent-bar {
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  opacity: 0.2;
  border-radius: 2px;
  z-index: 0;
}

.percent-text {
  position: relative;
  z-index: 1;
  padding-right: 1px;
  font-weight: 500;
}

.total-row {
  background-color: #f8f9fa;

  body.dark-layout & {
    background-color: #283046;
  }

  td {
    border-top: 2px solid #e2e8f0;
    border-bottom: none;
    padding-top: 10px;
    padding-bottom: 10px;

    body.dark-layout & {
      border-top: 2px solid #3b4253;
    }
  }
}

tfoot {
  scroll-snap-align: end;
  position: sticky;
  bottom: 0;
  background-color: #fff;
  z-index: 1;
  box-shadow: 0 -4px 6px -6px rgba(0, 0, 0, 0.1);

  body.dark-layout & {
    background-color: #283046;
    box-shadow: 0 -4px 6px -6px rgba(0, 0, 0, 0.25);
  }
}

.color-cell {
  width: 16px;
  height: 16px;
  padding: 0;
}

.color-indicator {
  display: inline-block;
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-top: 5px;
}
</style>
